'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
  useTransition,
  type ReactNode,
} from 'react';
import { captureException } from '../../../lazy-sentry';

export type StartNavigation = (navigation: () => void) => void;
type LanguageChange = (startNavigation: StartNavigation, isActive: () => boolean) => Promise<void>;

const LanguageChangeContext = createContext<{
  isChanging: boolean;
  runLanguageChange: (change: LanguageChange) => Promise<void>;
} | null>(null);

export function LanguageChangeProvider({ children }: { children: ReactNode }) {
  const locked = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isNavigating, startTransition] = useTransition();
  const isChanging = isSaving || isNavigating;

  useEffect(() => {
    if (!isChanging) {
      locked.current = false;
    }
  }, [isChanging]);

  // The save stays outside the transition: a pending async transition would entangle every
  // navigation the reader starts meanwhile, holding their URL until the save settles.
  const runLanguageChange = useCallback(async (change: LanguageChange) => {
    if (locked.current) {
      return;
    }
    locked.current = true;
    setIsSaving(true);
    let active = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        change(
          (navigation) => {
            if (active) {
              startTransition(navigation);
            }
          },
          () => active,
        ),
        new Promise<never>((_resolve, reject) => {
          timeout = setTimeout(() => reject(new Error('Language change timed out')), 5000);
        }),
      ]);
    } catch (error) {
      captureException(error);
    } finally {
      active = false;
      clearTimeout(timeout);
      setIsSaving(false);
    }
  }, []);

  const value = useMemo(() => ({ isChanging, runLanguageChange }), [isChanging, runLanguageChange]);

  return <LanguageChangeContext.Provider value={value}>{children}</LanguageChangeContext.Provider>;
}

export function useLanguageChange() {
  const context = useContext(LanguageChangeContext);
  if (!context) {
    throw new Error('LanguageSelect requires LanguageChangeProvider');
  }
  return context;
}
